'use client';
import { cn } from '@ngg/storefront-utils';
import { ElementType } from 'react';
import { Dictionary } from '@ts/dictionary';
import * as styles from './PagePagination.styles';
import { useSearchParams } from 'next/navigation';
import PageLinkButtons from './PageLinkButtons';
import PaginationArrowButtons from './PaginationArrowButtons';

type PaginationProps = {
  page: number;
  slug: string;
  totalPages?: number;
  pageRange?: number;
  PaginationComponent?: ElementType;
  dictionary: Dictionary;
  pageNumber: number;
};

const PagePagination = ({
  page,
  slug,
  pageRange = 3,
  totalPages = 0,
  PaginationComponent = 'div',
  dictionary,
  pageNumber,
}: PaginationProps) => {
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();

  if (totalPages <= 1) return null;

  const prevDisabled = page <= 1;
  const nextDisabled = page === totalPages;

  return (
    <nav aria-label="Pagination" className={styles.nav()}>
      <PaginationComponent className={cn(styles.innerContainer())}>
        <PaginationArrowButtons
          variants="backward"
          disabled={prevDisabled}
          arrowText={dictionary?.previousPage}
          fastNavigationArrowIcon="firstPageArrow"
          nextNavigationArrowIcon="leftArrow"
          fastNavigationArrowLink={`${slug}${
            searchParamsString ? `?${searchParamsString}` : ''
          }`}
          nextNavigationArrowLink={`${slug}${
            pageNumber > 2 ? `/${pageNumber - 1}` : ''
          }${searchParamsString ? `?${searchParamsString}` : ''}`}
        />
        <PageLinkButtons
          pageRange={pageRange}
          slug={slug}
          page={pageNumber}
          totalPages={totalPages}
          goToPage={dictionary.goToPage}
        />

        <PaginationArrowButtons
          variants="forward"
          disabled={nextDisabled}
          arrowText={dictionary?.nextPage}
          fastNavigationArrowIcon="lastPageArrow"
          nextNavigationArrowIcon="rightArrow"
          fastNavigationArrowLink={`${slug}/${totalPages}${
            searchParamsString ? `?${searchParamsString}` : ''
          }`}
          nextNavigationArrowLink={`${slug}/${Math.min(page + 1, totalPages)}${
            searchParamsString ? `?${searchParamsString}` : ''
          }`}
        />
      </PaginationComponent>
    </nav>
  );
};

export default PagePagination;
